// src/hooks/useEditorAutosave.js
//
// Dual-layer autosave for the text editor:
//   Layer 1 – localStorage snapshot every 400 ms (instant safety net)
//   Layer 2 – REST PUT to server 3 s after the last change, plus every 60 s
//             while changes keep coming (source-of-truth sync)
//
// Also flushes via a keepalive PUT on tab close / visibility-hidden / unmount,
// and asks before the tab closes with changes the server doesn't have yet.

import { useRef, useState, useEffect, useCallback } from 'react';
import {
  setLocalDraft, clearLocalDraft, hashContent, makeVisitId,
} from '../utils/editorStorage';
import { API_BASE } from '../utils/api';
import { getCleanHTML } from '../utils/editorSerialize';
import { manuscriptWordStats } from '../utils/wordStats';
import { useToast } from '../context/ToastContext';

/**
 * Does this HTML carry any text a reader would see?
 *
 * The one rule that protects a manuscript: never write a document with no
 * visible text over one that has some. A contentEditable passes through
 * genuinely empty states that are still structurally valid HTML — mid
 * re-pagination, during a bulk DOM replacement, before the loaded draft has
 * been injected — and `html` is a truthy string in every one of them. A bare
 * falsy check does not catch any of it.
 *
 * Exported because the periodic save is not the only writer. Every path that
 * PUTs content has to ask this question, and the ones that forgot are how a
 * populated draft comes back empty.
 */
export function hasVisibleText(html) {
  if (!html) return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  // Embedded media counts as content even though it contributes no text.
  // A plate, a map, a chapter-opening illustration or a scanned signature is
  // a real page of a manuscript, and <img> is a void element with no text
  // node — so a text-only test cannot tell that page apart from an empty
  // shell. Without this the guard would trade one kind of data loss for
  // another: instead of overwriting the page it would silently refuse to
  // save it, which is worse for being invisible.
  if (tmp.querySelector('img, figure, canvas, svg, video')) return true;

  // <style> and <script> text is not read by anyone. It is counted by
  // textContent, so a document carrying nothing but a stylesheet would
  // otherwise pass the guard and be written over real content.
  tmp.querySelectorAll('style, script').forEach((el) => el.remove());

  // Strip zero-width spaces: the editor uses them as carets and they are not
  // text a reader would miss.
  return tmp.textContent.replace(/​/g, '').trim().length > 0;
}

const LOCAL_DEBOUNCE_MS = 400;
// A pause this long after the last change saves to the server. With only the
// 60 s sync, up to a minute of work — text, and the block ids comments anchor
// to — lived only in this tab, and a reload in that window lost it: comments
// made on new paragraphs came back "Unassigned".
const SERVER_DEBOUNCE_MS = 3_000;
const SERVER_SYNC_MS = 60_000; // backstop while typing without a pause

/**
 * @param {string}          draftId
 * @param {React.RefObject} editorRef   – ref to the contentEditable host
 * @param {string|null}     accessToken – JWT Bearer token
 * @param {React.RefObject} [loadedRef] – true once the draft has been loaded
 *        into the editor. Until then nothing is saved, local or server: an
 *        editor whose load FAILED shows a blank page, and saving that page
 *        writes it over the real manuscript. Omitted = always loaded.
 * @returns {{ saveStatus: string, triggerChange: () => void, forceSave: (silent?: boolean) => Promise<void>,
 *   noteServerContent: (html: string) => void, getServerHash: () => string|null,
 *   connectionLost: boolean, localBackupOk: boolean, visitId: string }}
 */
export default function useEditorAutosave(draftId, editorRef, accessToken, loadedRef) {
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'unsaved' | 'saving' | 'error'
  // The last save never reached the server (offline, dropped connection), as
  // opposed to the server answering with an error.
  const [connectionLost, setConnectionLost] = useState(false);
  // Whether the browser accepted the last local snapshot. When it didn't, the
  // author must not be told their changes are kept on this computer.
  const [localBackupOk, setLocalBackupOk] = useState(true);
  // hashContent() of the version the server holds, recorded in each snapshot.
  const serverHashRef = useRef(null);
  // This visit's own snapshot key. Snapshots other visits left are never
  // touched here — they are the editor's to offer back.
  const visitIdRef = useRef(null);
  if (!visitIdRef.current) visitIdRef.current = makeVisitId();
  const dirtyRef = useRef(false);
  // Bumped on every change, so a save can tell whether the document moved on
  // while its request was in flight.
  const changeSeqRef = useRef(0);
  // Tail of the save queue. Saves run one at a time: two overlapping PUTs can
  // land out of order and leave the older content on the server.
  const saveChainRef = useRef(Promise.resolve());
  const localTimerRef = useRef(null);
  const serverTimerRef = useRef(null);
  const serverIntervalRef = useRef(null);
  const toast = useToast();

  // Always read the latest content from the DOM, stripped of view-only
  // decorations (formatting marks, etc.) via the shared serialization boundary.
  const getContent = useCallback(
    () => getCleanHTML(editorRef.current),
    [editorRef],
  );

  // Whatever is in the editor before the draft loaded is not the draft.
  const isLoaded = useCallback(() => !loadedRef || !!loadedRef.current, [loadedRef]);

  // ---- server persist ---------------------------------------------------
  const saveNow = useCallback(async (silent) => {
    if (!draftId || !isLoaded()) return;
    const html = getContent();
    if (!html) return;

    // Guard: never overwrite real content with a blank page.
    if (!hasVisibleText(html)) return;

    const seq = changeSeqRef.current;
    setSaveStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/api/drafts/${draftId}`, {
        method: 'PUT',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        // The counts word goals read, taken from the same DOM as `html` in the
        // same moment. Only on this save: the tab-close flush is size-capped,
        // and the next save records them anyway.
        body: JSON.stringify({ content: html, wordStats: manuscriptWordStats(editorRef.current) }),
      });

      if (res.ok) {
        setConnectionLost(false);
        serverHashRef.current = hashContent(html);
        // A change made while this request was in flight isn't in it. Stay
        // dirty so the save it scheduled still runs, instead of reporting
        // "Saved" over work the server doesn't have.
        if (changeSeqRef.current === seq) {
          dirtyRef.current = false;
          clearLocalDraft(draftId, visitIdRef.current);
          setSaveStatus('saved');
        } else {
          // Newer changes arrived mid-flight. Re-take the snapshot now, on top
          // of what was just stored — relabelling the old one would pair a
          // possibly older text with the newer base.
          const cur = getContent();
          if (cur) setLocalBackupOk(setLocalDraft(draftId, cur, serverHashRef.current, visitIdRef.current));
          setSaveStatus('unsaved');
        }
        if (!silent) toast.success('Progress saved!');
      } else {
        setConnectionLost(false); // the server answered: we're connected
        setSaveStatus('error');
        if (!silent) {
          const msg = await res.text().catch(() => '');
          toast.error('Failed to save progress. ' + msg);
        }
      }
    } catch {
      // fetch only throws when the request never got an answer.
      setConnectionLost(true);
      setSaveStatus('error');
      if (!silent) toast.error('Failed to save progress.');
    }
  }, [draftId, accessToken, editorRef, getContent, toast, isLoaded]);

  const persistToServer = useCallback((silent = true) => {
    const run = saveChainRef.current.then(() => saveNow(silent));
    saveChainRef.current = run.catch(() => {});
    return run;
  }, [saveNow]);
  // The 3 s timer calls through this, so a token refreshed while it waits is
  // the one its save uses.
  const persistRef = useRef(persistToServer);
  persistRef.current = persistToServer;

  // ---- called on every content change ------------------------------------
  const triggerChange = useCallback(() => {
    // Not the draft yet (still loading, or the load failed): not a change to it.
    if (!isLoaded()) return;
    changeSeqRef.current += 1;
    dirtyRef.current = true;
    setSaveStatus('unsaved');

    // Layer 1: debounce to localStorage
    if (localTimerRef.current) clearTimeout(localTimerRef.current);
    localTimerRef.current = setTimeout(() => {
      // A save that landed in the meantime already holds this change; a copy
      // now would be a leftover identical to the server.
      if (!dirtyRef.current) return;
      const html = getContent();
      if (html) setLocalBackupOk(setLocalDraft(draftId, html, serverHashRef.current, visitIdRef.current));
    }, LOCAL_DEBOUNCE_MS);

    // Layer 2: save once the author pauses
    if (serverTimerRef.current) clearTimeout(serverTimerRef.current);
    serverTimerRef.current = setTimeout(() => {
      serverTimerRef.current = null;
      if (dirtyRef.current) persistRef.current(true);
    }, SERVER_DEBOUNCE_MS);
  }, [draftId, getContent, isLoaded]);

  // ---- force save (manual save / before publish) -------------------------
  // silent=false (the default) surfaces a toast — used for user-initiated
  // saves (button click, Ctrl+S). Pass true for saves that shouldn't
  // interrupt the user (e.g. pre-publish flush).
  const forceSave = useCallback(async (silent = false) => {
    if (localTimerRef.current) {
      clearTimeout(localTimerRef.current);
      localTimerRef.current = null;
    }
    if (serverTimerRef.current) {
      clearTimeout(serverTimerRef.current);
      serverTimerRef.current = null;
    }
    await persistToServer(silent);
  }, [persistToServer]);

  // ---- periodic server sync (every 60 s) --------------------------------
  useEffect(() => {
    serverIntervalRef.current = setInterval(() => {
      if (dirtyRef.current) {
        persistToServer(true); // silent
      }
    }, SERVER_SYNC_MS);

    return () => {
      if (serverIntervalRef.current) clearInterval(serverIntervalRef.current);
    };
  }, [persistToServer]);

  // ---- flush on tab close / visibility hidden / unmount ------------------
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current || !draftId || !isLoaded()) return;
      const html = getContent();
      // Same guard as the periodic save. This path had only a falsy check,
      // which is exactly the gap described on hasVisibleText: a tab closing
      // or being hidden mid-repagination would beacon a textless document
      // over a finished manuscript, with no way to undo it.
      if (!hasVisibleText(html)) return;

      const url = `${API_BASE}/api/drafts/${draftId}`;
      const payload = JSON.stringify({ content: html });

      // Not sendBeacon: it can only POST and can't carry the bearer token, so
      // the server refused every one (405) — and because the browser reports a
      // queued beacon as sent, this keepalive PUT never ran. keepalive is still
      // capped (~64 KB in Chrome), so a long manuscript can't be saved here;
      // the 3 s save keeps that window short, and closing the tab asks first.
      try {
        fetch(url, {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };

    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      flush();
      // Ask before leaving with changes the server doesn't have yet.
      e.preventDefault();
      e.returnValue = '';
    };
    const onVisChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisChange);
      // Final flush on unmount (route change, etc.)
      flush();
      if (localTimerRef.current) clearTimeout(localTimerRef.current);
    };
  }, [draftId, accessToken, getContent, isLoaded]);

  // Back online: save what piled up rather than waiting for the next change
  // or the 60 s sync.
  useEffect(() => {
    const onOnline = () => {
      if (dirtyRef.current && isLoaded()) persistRef.current(true);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [isLoaded]);

  // The editor tells us what the server held when the draft loaded, so local
  // snapshots can record which version they build on.
  const noteServerContent = useCallback((html) => {
    serverHashRef.current = hashContent(html ?? '');
  }, []);
  // The version the server holds as far as this visit knows — the loaded one,
  // or this visit's last successful save.
  const getServerHash = useCallback(() => serverHashRef.current, []);

  // Cancel a pending 3 s save on unmount only. The effect above also re-runs
  // when the token refreshes, and clearing it there dropped that save until
  // the 60 s backstop.
  useEffect(() => () => {
    if (serverTimerRef.current) clearTimeout(serverTimerRef.current);
  }, []);

  return {
    saveStatus, triggerChange, forceSave, noteServerContent, getServerHash,
    connectionLost, localBackupOk, visitId: visitIdRef.current,
  };
}
